import {LoadingContent} from "../components/LoadingContent";
import useApiCall from "../hooks/CancellableApiCall";
import useUser from "../hooks/useUser";
import {Box, Button, Grid, Pagination, Paper, Typography} from "@mui/material";
import React, {useMemo, useState} from "react";
import {Link as RouterLink, useSearchParams} from "react-router-dom";
import {HelpdeskPageninatedResponse} from "../data/helpdesk/HelpdeskPageninatedResponse";
import {Division} from "../data/Division";
import {HelpdeskFilterPanel, FilterState, emptyFilters, buildSearchUrl, isFiltering} from "../components/helpdesk/HelpdeskFilterPanel";
import {HelpdeskTicketTable} from "../components/helpdesk/HelpdeskTicketTable";

export const HelpdeskPage = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    const parseFiltersFromParams = (params: URLSearchParams): FilterState => ({
        query: params.get("query") ?? "",
        division: params.get("division") ?? "",
        statuses: params.getAll("status").map(s => s.toLowerCase()),
        assignee: params.get("assignee") ?? ""
    })

    const [page, setPage] = useState(parseInt(searchParams.get("page") ?? "1"))
    const [filters, setFilters] = useState<FilterState>(parseFiltersFromParams(searchParams))

    const updateSearchParams = (newFilters: FilterState, newPage: number) => {
        const params = new URLSearchParams()
        if (newPage > 1) params.set("page", String(newPage))
        if (newFilters.query) params.set("query", newFilters.query)
        if (newFilters.division) params.set("division", newFilters.division)
        if (newFilters.assignee) params.set("assignee", newFilters.assignee)
        newFilters.statuses.forEach(s => params.append("status", s))
        setSearchParams(params, {replace: true})
    }

    const handleFiltersChange = (f: FilterState) => {
        setFilters(f)
        setPage(1)
        updateSearchParams(f, 1)
    }

    const handleFiltersClear = () => {
        setFilters(emptyFilters)
        setPage(1)
        updateSearchParams(emptyFilters, 1)
    }

    const divisionsData = useApiCall<Division[]>({url: "/api/division/list-divisions"})

    const divisionsUrl = useMemo(() => {
        if (filters.statuses.length > 0) {
            const params = new URLSearchParams()
            filters.statuses.forEach(s => params.append("status", s.toUpperCase()))
            return `/api/helpdesk/divisions?${params.toString()}`
        }
        return "/api/helpdesk/divisions"
    }, [filters.statuses])

    const visibleDivisionIdentifiers = useApiCall<string[]>({url: divisionsUrl})

    const visibleDivisions = (divisionsData.data ?? []).filter(d =>
        (visibleDivisionIdentifiers.data ?? []).includes(d.identifier)
    )

    const userData = useUser()

    const searching = isFiltering(filters)
    const searchUrl = useMemo(() => buildSearchUrl(filters, page), [filters, page])

    const browseData = useApiCall<HelpdeskPageninatedResponse>({
        url: searching ? undefined : `/api/helpdesk/?page=${page}`,
        deferLoading: searching
    })

    const searchData = useApiCall<HelpdeskPageninatedResponse>({
        url: searching ? searchUrl : undefined,
        deferLoading: !searching
    })

    const ticketData = searching ? searchData.data : browseData.data
    const isLoading = searching ? searchData.isLoading : browseData.isLoading
    const totalPages = ticketData?.pages ?? 1

    const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value)
        updateSearchParams(filters, value)
    }

    return <LoadingContent isLoading={userData.isLoading}>
        <Typography variant={"h6"} gutterBottom>Paralake Police Helpdesk</Typography>
        <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={9}>
                <Paper variant={"outlined"} style={{padding: "16px"}}>
                    <LoadingContent isLoading={isLoading}>
                        <HelpdeskTicketTable ticketData={ticketData ?? {tickets: [], totalTickets: 0, pages: 1}}/>
                    </LoadingContent>
                    <Box sx={{mt: 1, display: "flex", gap: 1}}>
                        <Button component={RouterLink} to={`/helpdesk/create`} color="success">New Ticket</Button>
                    </Box>
                </Paper>
                <Box sx={{justifyContent: "center", display: "flex", mt: 2, width: "100%"}}>
                    <Pagination color={"secondary"} count={totalPages} page={page} style={{padding: 8}}
                                onChange={handleChangePage}/>
                </Box>
            </Grid>

            <Grid item xs={12} md={3}>
                <Paper variant={"outlined"} style={{padding: "16px"}}>
                    <HelpdeskFilterPanel
                        filters={filters}
                        divisions={visibleDivisions}
                        onChange={handleFiltersChange}
                        onClear={handleFiltersClear}
                    />
                </Paper>
            </Grid>
        </Grid>
    </LoadingContent>
}
